import React, { useContext, useState, useEffect } from 'react';
import DepositCss from '../css/DepositCss';
import { useNavigate } from 'react-router-dom';
import { Context } from '../Context/Context';
import { Flip, toast } from 'react-toastify';
import { getResult } from '../Api/GetResult';
import { setResult } from '../Api/SetResult';
import { resetResult } from '../Api/ResetResult';
import { Helmet } from 'react-helmet';

export default function UpdateResult() {
    const navigate = useNavigate();
    const { setLoad, setLoadColor } = useContext(Context);

    const [selectedGame, setSelectedGame] = useState('');
    const [gameResult, setGameResult] = useState('');
    const [gameResultData, setGameResultData] = useState(null);
    const [selectedData, setSelectedData] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0]; // "YYYY-MM-DD"
    });

    useEffect(() => {
        const fetchGameResult = async () => {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userinfo'));
                if (!userInfo?.username || !userInfo?.userId) {
                    toast.error("User info missing. Please login again.", {
                        autoClose: 2000,
                        theme: "colored",
                        transition: Flip
                    });
                    navigate('/login');
                    return;
                }

                const requestData = {
                    username: userInfo.username,
                    user_id: userInfo.userId,
                    date: selectedData
                };

                const response = await getResult(requestData);
                if (response.data.status && response.data?.data.game_result) {
                    setGameResultData(response.data.data.game_result);
                } else {
                    setGameResultData([]); // Reset on failed fetch
                    toast.error("Failed to load game result", {
                        autoClose: 1500,
                        theme: "colored",
                        transition: Flip
                    });
                }

            } catch (error) {
                setGameResultData([]); // Ensure UI clears on error
                toast.error("Something went wrong while fetching result", {
                    autoClose: 1500,
                    theme: "colored",
                    transition: Flip
                });
            }
        };

        fetchGameResult();
    }, [navigate, selectedData]); // ✅ trigger fetch on date change

    const handleUpdateResult = async () => {
        if (!selectedGame.trim() || !gameResult.trim()) {
            toast.error("Please select a game and enter the result", {
                autoClose: 1500,
                theme: "colored",
                transition: Flip
            });
            return;
        }

        setLoad(true);
        setLoadColor("#434343");

        try {
            const userInfo = JSON.parse(localStorage.getItem('userinfo'));
            if (!userInfo?.username || !userInfo?.userId) {
                toast.error("User info missing. Please login again.", {
                    autoClose: 2000,
                    theme: "colored",
                    transition: Flip
                });
                navigate('/login');
                return;
            }

            if (!isNaN(gameResult) && Number(gameResult) > 100) {
                toast.error("Number cannot be greater than 100", {
                    autoClose: 1500,
                    theme: "colored",
                    transition: Flip
                });
                return;
            }

            const data = {
                username: userInfo.username,
                user_id: userInfo.userId,
                game_name: selectedGame,
                result: gameResult,
                date: selectedData
            };

            const res = await setResult(data);

            if (res.data.status) {
                toast.success("Result updated successfully!", {
                    autoClose: 2000,
                    theme: "colored",
                    transition: Flip
                });
                navigate('/game');
            } else {
                toast.error(res.data.message || "Update failed", {
                    autoClose: 1500,
                    theme: "colored",
                    transition: Flip
                });
            }

        } catch (error) {
            toast.error("Something went wrong", {
                autoClose: 1500,
                theme: "colored",
                transition: Flip
            });
        } finally {
            setLoad(false);
        }
    };

    const handleResetAll = async () => {
        setLoad(true);
        setLoadColor("#434343");

        try {
            const userInfo = JSON.parse(localStorage.getItem('userinfo'));
            if (!userInfo?.username || !userInfo?.userId) {
                toast.error("User info missing. Please login again.", {
                    autoClose: 2000,
                    theme: "colored",
                    transition: Flip
                });
                navigate('/login');
                return;
            }

            const resetData = {
                username: userInfo.username,
                user_id: userInfo.userId
            };

            const res = await resetResult(resetData);

            if (res.data.status) {
                toast.success("All results reset successfully!", {
                    autoClose: 2000,
                    theme: "colored",
                    transition: Flip
                });
                setGameResultData(res.data.data?.game_result || []);
                setSelectedGame('');
                setGameResult('');
                navigate('/game');
            } else {
                toast.error(res.data.message || "Reset failed", {
                    autoClose: 1500,
                    theme: "colored",
                    transition: Flip
                });
            }
        } catch (error) {
            toast.error("Something went wrong while resetting", {
                autoClose: 1500,
                theme: "colored",
                transition: Flip
            });
        } finally {
            setLoad(false);
        }
    };

    return (
        <DepositCss>
            <Helmet>
                {/* Page Title */}
                <title>Update Result | Update Bazar Game Results | Matka Jantri</title>

                {/* SEO Description */}
                <meta
                    name="description"
                    content="Update the bazar results of different Matka games added in the Add Game section. Keep your game results accurate and up-to-date on MatkaJantri."
                />

                {/* SEO Directives */}
                <meta name="robots" content="index, follow" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://app.matkajantri.com/update-result" />

                {/* Favicon and icons */}
                <link rel="icon" href="/favicon.ico" type="image/x-icon" />
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                {/* Mobile Optimization */}
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />

                {/* Open Graph */}
                <meta property="og:title" content="Update Result | Update Bazar Game Results | MatkaJantri" />
                <meta
                    property="og:description"
                    content="Update the bazar results of different Matka games added in the Add Game section. Keep your game results accurate and up-to-date on MatkaJantri."
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://app.matkajantri.com" />
                <meta property="og:image" content="https://app.matkajantri.com/Logo.png" />
                <meta name="google-site-verification" content="d6d81656210c6db7" />
            </Helmet>
            <div className="w-100 fullpage" style={{ padding: '10px' }}>
                <div className="w-3">
                    <div className="back-top">
                        <i className="fa-solid fa-chevron-left" onClick={() => navigate("/game")}></i>
                    </div>

                    <div className="w-100 text-center d-flex">
                        <button className="addFunds">
                            <i className="fa-solid fa-trophy"></i> Update Game Result
                        </button>
                    </div>

                    <div className="alert alert-warning" style={{ fontSize: "14px", lineHeight: "1.5", marginBottom: "20px", padding: "15px" }}>
                        <strong>Note:-</strong> पहले आप जंत्री भरें उसके बाद रिजल्ट अपडेट करें।<br />
                        * नया, रिजल्ट अपडेट करने से पहले <strong>Reset All</strong> ज़रूर करें।
                    </div>

                    <div className="w-100 px-3">

                        <div className="mb-3 w-100">
                            <label className="form-label">Select Date <span style={{ color: 'red' }}>*</span></label>
                            <input
                                type="date"
                                className="form-control"
                                value={selectedData}
                                onChange={(e) => setSelectedData(e.target.value)}
                                max={new Date().toISOString().split('T')[0]}
                            />
                        </div>

                        {gameResultData?.length > 0 ? (
                            <div className="mb-3">
                                <div className="d-flex justify-content-between px-2 mb-2">
                                    <div className="game-header-left">
                                        <strong>Game Name</strong>
                                    </div>
                                    <div className="game-header-right text-end">
                                        <strong>Result</strong>
                                    </div>
                                </div>
                                <div className="game-list">
                                    {gameResultData.map((game, idx) => {
                                        const isValidNumber =
                                            game.result && !isNaN(game.result) && Number(game.result) !== 0;
                                        return (
                                            <div key={idx} className="game-box">
                                                <div className="game-name">{game.game_name}</div>
                                                <div className="game-result">
                                                    {isValidNumber ? (
                                                        <span className="result-success">{game.result}</span>
                                                    ) : (
                                                        <span className="result-fail">
                                                            <i className="fa-solid fa-times-circle"></i>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                        ) : (
                            <p>No Data Found...</p>
                        )}

                        <div className="mb-3 w-100">
                            <label className="form-label">Select Game <span style={{ color: 'red' }}>*</span></label>
                            <select
                                className="form-control"
                                value={selectedGame}
                                onChange={(e) => setSelectedGame(e.target.value)}
                            >
                                <option value="">-- Select Game --</option>
                                {gameResultData?.map((game, idx) => (
                                    <option key={idx} value={game.game_name}>
                                        {game.game_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mb-3 w-100">
                            <label className="form-label">Result <span style={{ color: 'red' }}>*</span></label>
                            <input
                                type="text"
                                className="form-control ps-2"
                                value={gameResult}
                                onChange={(e) => setGameResult(e.target.value)}
                                placeholder="Enter result (e.g. 24, WIN, etc.)"
                            />
                        </div>

                        <div className="text-center d-flex justify-content-center gap-3">
                            <button className="submit-btn support-btn" onClick={handleUpdateResult}>
                                Update Result
                            </button>
                            <button className="submit-btn support-btn bg-danger" onClick={handleResetAll}>
                                Reset All
                            </button>
                        </div>
                    </div>

                    <div className="alert alert-warning" style={{ fontSize: "14px", lineHeight: "1.5", marginBottom: "20px", padding: "15px" }}>
                        <strong>Note:-</strong> जंत्री भरने के बाद कृपया यहाँ अपना रिज़ल्ट अपडेट करें।<br />
                        * रिज़ल्ट अपडेट करते ही आपका पूरा हिसाब-किताब एडमिन पैनल में सेव हो जाएगा।<br />
                        * इसके बाद अगर आप जंत्री डिलीट भी कर दें, तब भी आपका डेटा एडमिन पैनल में सुरक्षित रहेगा।<br />
                        * आपको पूरे महीने का हिसाब-किताब वहीं पर मिलता रहेगा।
                    </div>
                </div>
            </div>
        </DepositCss>
    );
}
